/**
 * HowItWorks — «كيف تشتغل»: three hairline columns with small illustrations
 * (phone: icon rows). Also exports StepsPlain for the guide pages.
 */
import React from 'react';
import {
  GoogleIcon, ImageUploadIcon, PaintBrush02Icon, QrCodeIcon, Tick02Icon, UserAdd01Icon, UserCircleIcon
} from '@hugeicons/core-free-icons';
import Icon from '../../components/v2/Icon.jsx';
import { FakeQR } from '../../components/WalletPass.jsx';
import { useStr } from './useStr.js';
import { ME_DARK, PHOTO_URL } from './data.js';
import PassSlot from './PassSlot.jsx';

const L = ({ children, className = '' }) => <span className={`ltr${className ? ` ${className}` : ''}`}>{children}</span>;

export const STEP_ICONS = [
  { icon: UserAdd01Icon, g: 'g2' },
  { icon: PaintBrush02Icon, g: 'g3' },
  { icon: QrCodeIcon, g: 'g1' }
];

function StepTop({ i }) {
  const { icon, g } = STEP_ICONS[i];
  return (
    <div className="stop">
      <span className={`ic-t ${g}`}><Icon icon={icon} /></span>
      <span className="sn ltr">{String(i + 1).padStart(2, '0')}</span>
    </div>
  );
}

export default function HowItWorks() {
  const { t, ar } = useStr();
  return (
    <section className="sx">
      <div className="wrap">
        <div className="shead" id="how">
          <div>
            <div className="eb">{t('howEb')}</div>
            <h2 className="h">{t('howTitle')}</h2>
          </div>
          <p className="ssub">
            {ar
              ? <>سجّل، صمّم، وشارك. بطاقتك جاهزة في أقل من <span className="nw"><L className="num">30</L> ثانية.</span></>
              : <>Sign up, design, share. Your card is ready in under <span className="nw">30 seconds.</span></>}
          </p>
        </div>
        <div className="steps">
          <div className="step">
            <StepTop i={0} />
            <div className="viz" aria-hidden="true">
              <div className="vin">
                <div className="mini su">
                  <b>{t('suTitle')}</b>
                  <div className="in"><L>you@email.com</L></div>
                  <span className="btn full soft">{t('suNext')}</span>
                  <div className="or">{t('suOr')}</div>
                  <span className="btn full"><Icon icon={GoogleIcon} />{ar ? <>المتابعة مع <L>Google</L></> : 'Continue with Google'}</span>
                </div>
              </div>
            </div>
            <h3>{ar ? <>سجّل في <L className="num">30</L> ثانية</> : 'Sign up in 30 seconds'}</h3>
            <p>{ar ? 'إيميل أو Google. بدون بطاقة ائتمان. أنت وبطاقتك بس.' : 'Email or Google. No credit card. Just you and your card.'}</p>
          </div>

          <div className="step">
            <StepTop i={1} />
            <div className="viz" aria-hidden="true">
              <div className="vin">
                <div className="mini ed">
                  <div>
                    <div className="l">{t('edColor')}</div>
                    <div className="sw">
                      <i className="on" style={{ background: '#1c1c20' }} />
                      <i style={{ background: '#0C44EF' }} />
                      <i style={{ background: '#0F2E1F' }} />
                    </div>
                  </div>
                  <div className="up"><Icon icon={ImageUploadIcon} />{t('edLogo')}</div>
                  <div className="up"><Icon icon={UserCircleIcon} />{t('edPhoto')}</div>
                </div>
                <PassSlot card={ME_DARK} photoUrl={PHOTO_URL} scale={0.5} />
              </div>
            </div>
            <h3>{t('step2T')}</h3>
            <p>{t('step2D')}</p>
          </div>

          <div className="step">
            <StepTop i={2} />
            <div className="viz" aria-hidden="true">
              <div className="vin qrv">
                <div className="qrbox">
                  <i className="c c1" /><i className="c c2" /><i className="c c3" /><i className="c c4" />
                  <div className="qt"><FakeQR /></div>
                </div>
                <div className="toast"><span className="d"><Icon icon={Tick02Icon} /></span>{t('saved')}</div>
              </div>
            </div>
            <h3>{t('step3T')}</h3>
            <p>
              {ar ? (
                <>
                  أضفها لـ <span className="nw">Apple Wallet</span> أو <span className="nw">Google Wallet</span>، أظهر{' '}
                  <span className="nw">الـ <L>QR</L>،</span> يصوّرونه، وتنحفظ جهة اتصالك. خلصنا.
                </>
              ) : (
                <>Add it to <span className="nw">Apple Wallet</span> or <span className="nw">Google Wallet</span>, show the QR, they scan, your contact is saved. Done.</>
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Guide pages: the same hairline columns without illustrations (icons always shown). */
export function StepsPlain({ items, L: pickL }) {
  return (
    <div className="steps plain">
      {items.map((st, i) => (
        <div className="step" key={i}>
          <StepTop i={i % 3} />
          <h3>{pickL(st.t)}</h3>
          <p>{pickL(st.d)}</p>
        </div>
      ))}
    </div>
  );
}
